import { useState, useMemo, useCallback, useEffect } from 'react';
import type { Dayjs } from 'dayjs';
import {
  StatusFilter,
  FilterState,
  HistoryTableRow,
  ROWS_PER_PAGE,
  groupTransactionsForHistory,
  calculateHistoryAmountDue,
  isPayableInvoiceRow,
} from './Financials.utils';
import {
  IntacctInvoice,
  IntacctPayment,
  IntacctCreditMemo,
} from '../../../schema/generated/schema';

interface UseFinancialsFilterLogicProps {
  invoices: IntacctInvoice[];
  payments: IntacctPayment[];
  creditMemos: IntacctCreditMemo[];
}

interface UseFinancialsFilterLogicReturn {
  searchQuery: string;
  statusFilter: StatusFilter;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  filters: FilterState;

  currentPage: number;
  totalPages: number;

  paginatedRows: HistoryTableRow[];
  /** All rows matching current filters (not paginated); use for CSV export. */
  allFilteredRows: HistoryTableRow[];
  totalRowCount: number;
  historyAmountDue: number;

  resultCount: number;
  totalCount: number;

  selectedRowKeys: React.Key[];
  allUnpaidInvoiceRows: HistoryTableRow[];

  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleStatusChange: (value: StatusFilter) => void;
  handleDateFromChange: (date: Dayjs | null) => void;
  handleDateToChange: (date: Dayjs | null) => void;
  handleSelectionChange: (keys: React.Key[]) => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;

  hasAnyBillingData: boolean;
}

export const useFinancialsFilterLogic = ({
  invoices,
  payments,
  creditMemos,
}: UseFinancialsFilterLogicProps): UseFinancialsFilterLogicReturn => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const filters: FilterState = useMemo(
    () => ({
      searchQuery,
      statusFilter,
      dateFrom: dateFrom ? dateFrom.format('YYYY-MM-DD') : null,
      dateTo: dateTo ? dateTo.format('YYYY-MM-DD') : null,
    }),
    [searchQuery, statusFilter, dateFrom, dateTo]
  );

  // =========================================================================
  // History View Computations
  // =========================================================================

  const {
    history: historyEntries,
    unappliedCredits,
    filteredCreditMemos,
  } = useMemo(
    () => groupTransactionsForHistory(invoices, payments, filters, creditMemos),
    [invoices, payments, filters, creditMemos]
  );

  const historyAmountDue = useMemo(
    () => calculateHistoryAmountDue(historyEntries, filteredCreditMemos),
    [historyEntries, filteredCreditMemos]
  );

  const allRows: HistoryTableRow[] = useMemo(() => {
    const invoiceRows: HistoryTableRow[] = historyEntries.map((entry) => {
      const row: HistoryTableRow = {
        kind: 'invoice' as const,
        data: entry,
        key: `invoice-${entry.invoice.recordNo}`,
      };
      if (entry.appliedTransactions.length === 0) {
        return row;
      }
      return {
        ...row,
        children: entry.appliedTransactions.map((p, idx) => ({
          kind: 'appliedPayment' as const,
          data: p,
          key: `payment-${entry.invoice.recordNo}-${p.recordNo}-${idx}`,
        })),
      };
    });

    const creditRows: HistoryTableRow[] = unappliedCredits.map((credit) => ({
      kind: 'credit' as const,
      data: credit,
      key: `credit-${credit.recordNo}`,
    }));

    const creditMemoRows: HistoryTableRow[] = filteredCreditMemos.map((cm) => ({
      kind: 'creditMemo' as const,
      data: cm,
      key: `creditMemo-${cm.recordNo}`,
    }));

    if (statusFilter === 'Unapplied') return [...creditRows, ...creditMemoRows];
    if (statusFilter !== 'All') return invoiceRows;
    return [...invoiceRows, ...creditRows, ...creditMemoRows];
  }, [historyEntries, unappliedCredits, filteredCreditMemos, statusFilter]);

  const allUnpaidInvoiceRows: HistoryTableRow[] = useMemo(
    () => allRows.filter(isPayableInvoiceRow),
    [allRows]
  );

  const hasAnyBillingData = useMemo(
    () => invoices.length > 0 || payments.length > 0 || creditMemos.length > 0,
    [invoices.length, payments.length, creditMemos.length]
  );

  const totalRowCount = allRows.length;

  // =========================================================================
  // Pagination & Counts
  // =========================================================================

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    return allRows.slice(start, start + ROWS_PER_PAGE);
  }, [allRows, currentPage]);

  const totalPages = useMemo(
    () => Math.ceil(totalRowCount / ROWS_PER_PAGE),
    [totalRowCount]
  );

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const resultCount = paginatedRows.length;
  const totalCount = totalRowCount;

  // =========================================================================
  // Handlers
  // =========================================================================

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setCurrentPage(1);
    },
    []
  );

  const handleStatusChange = useCallback((value: StatusFilter) => {
    setStatusFilter(value);
    setCurrentPage(1);
  }, []);

  const handleDateFromChange = useCallback((date: Dayjs | null) => {
    setDateFrom(date);
    setCurrentPage(1);
  }, []);

  const handleDateToChange = useCallback((date: Dayjs | null) => {
    setDateTo(date);
    setCurrentPage(1);
  }, []);

  const handleSelectionChange = useCallback((keys: React.Key[]) => {
    setSelectedRowKeys(keys);
  }, []);

  return {
    searchQuery,
    statusFilter,
    dateFrom,
    dateTo,
    filters,

    currentPage,
    totalPages,

    paginatedRows,
    allFilteredRows: allRows,
    totalRowCount,
    historyAmountDue,

    resultCount,
    totalCount,

    selectedRowKeys,
    allUnpaidInvoiceRows,

    handleSearchChange,
    handleStatusChange,
    handleDateFromChange,
    handleDateToChange,
    handleSelectionChange,
    setCurrentPage,

    hasAnyBillingData,
  };
};
